import { FileExcelFilled, MergeFilled } from "@ant-design/icons"
import { Button, Checkbox, DatePicker, Flex, Space, Tooltip } from "antd"
import useNotification from "antd/es/notification/useNotification"
import dayjs from "dayjs"
import { useContext, useState } from "react"
import { PositionsContext } from "../../../context/PositionsContext"
import { usePositions } from "../../../hooks/usePositions"
import { colors, spaces } from "../../../utils/styles"
import { AssetNameFilter } from "../../atoms/input/AssetNameFilter"
import { AssetLocaleSelect } from "../../atoms/select/AssetLocaleSelect"
import { CustomerOrFundSelect, MODES } from "../../atoms/select/CustomerOrFundSelect"
import { PositionsModeSelect } from "../../atoms/select/PositionsModeSelect"
import { VisualizationTypeSelect } from "../../atoms/select/VisualizationTypeSelect"
import { AssetsPositionTable } from "../../molecules/tables/AssetsPositionTable/AssetsPositionTable"
import { PositionsByStructureTable } from "../../molecules/tables/PositionsByStructureTable"

export const Positions = () => {
  const { handleExport } = usePositions()
  const [_, contextHolder] = useNotification()

  const {
    positions,
    datesAvailable,
    referenceDate,
    setReferenceDate,
    hideEmptyClasses,
    setHideEmptyClasses,
    isToShowOnlyAssets,
    setIsToShowOnlyAssets,
    isExplodedView,
    setIsExplodedView,
    isStructuresView,
    setIsStructuresView,
    isToExpandAll,
    setIsToExpandAll,
    visualizationType,
    setVisualizationType,
    customerOrFund,
    setCustomerOrFund,
    locale,
    setLocale,
    mode,
    setMode,
    assetName,
    setAssetName,
  } = useContext(PositionsContext)

  const isAllowedDate = (current) => {
    return datesAvailable.map((date) => dayjs(date)).some((date) => date.isSame(current, "day"))
  }

  return (
    <>
      {contextHolder}
      <Flex
        gap={spaces.space2}
        style={{ width: "max-content", height: "100%", paddingLeft: spaces.space2, paddingRight: spaces.space2, paddingTop: spaces.space2 }}
        vertical
      >
        <Flex style={{ gap: spaces.space2, alignItems: "center" }}>
          <AssetNameFilter setValue={setAssetName} width={"100%"} />
          <VisualizationTypeSelect value={visualizationType} setValue={setVisualizationType} disabled={isStructuresView} />
          <AssetLocaleSelect value={locale} setValue={setLocale} disabled={isStructuresView} />
          <PositionsModeSelect value={mode} setValue={setMode} disabled={isStructuresView} />
          <CustomerOrFundSelect mode={mode} value={customerOrFund} setValue={setCustomerOrFund} width={195} />
        </Flex>
        <Flex gap={spaces.space2}>
          <Space style={{ width: 642, gap: spaces.space4 }}>
            <Checkbox
              checked={hideEmptyClasses}
              onChange={(e) => setHideEmptyClasses(e.target.checked)}
              disabled={isToShowOnlyAssets || isStructuresView}
            >
              Hide empty classes
            </Checkbox>
            <Checkbox checked={isToShowOnlyAssets} onChange={(e) => setIsToShowOnlyAssets(e.target.checked)} disabled={isStructuresView}>
              Show only assets
            </Checkbox>
            <Tooltip title="Explodes positions through TERA/VRB funds">
              <Checkbox checked={isExplodedView} onChange={(e) => setIsExplodedView(e.target.checked)} disabled={isStructuresView}>
                Exploded view
              </Checkbox>
            </Tooltip>
            <Tooltip title="Show structures view (only available when a customer is selected)">
              <Checkbox
                disabled={!customerOrFund || mode !== MODES.CUSTOMER}
                checked={isStructuresView}
                onChange={(e) => setIsStructuresView(e.target.checked)}
              >
                Structures view
              </Checkbox>
            </Tooltip>
          </Space>
          <Space style={{ gap: spaces.space1 }}>
            <Button onClick={() => setIsToExpandAll(!isToExpandAll)} disabled={isToShowOnlyAssets} type="primary" icon={<MergeFilled />}>
              {isToExpandAll ? " Collapse all" : " Expand all"}
            </Button>
            <Tooltip title="Export will bring the list of assets and classes as columns, according to the selected filters.">
              <Button
                type="primary"
                style={{ backgroundColor: colors.green }}
                icon={<FileExcelFilled />}
                onClick={handleExport}
                disabled={isStructuresView}
              >
                Export
              </Button>
            </Tooltip>
          </Space>
          <Space>
            <b>Reference Date:</b>
            <DatePicker
              disabledDate={(current) => !isAllowedDate(current)}
              value={dayjs(referenceDate)}
              onChange={(e) => setReferenceDate(e.format("YYYY-MM-DD"))}
              allowClear={false}
              format={"DD/MM/YYYY"}
              style={{ width: 125 }}
            />
          </Space>
        </Flex>
        {isStructuresView ? (
          <PositionsByStructureTable positions={positions} />
        ) : (
          <AssetsPositionTable
            columnWidth={mode === MODES.CUSTOMER ? 150 : 200}
            expandAll={isToExpandAll || assetName !== ""}
            showOnlyAssets={isToShowOnlyAssets}
            enableSort={customerOrFund}
            data-hj-suppress
          />
        )}
      </Flex>
    </>
  )
}
